// apps/frontend-vario-kaufland/src/lib/category-modal.ts
//
// Reine, DOM-freie Logik des Kategorie-Modals "Kategorien waehlen" (settings.ts).
// Ergaenzt category-tree.ts (Aufbau und sichtbare Zeilen des Baums) um das, was der
// Entwurf vom 2026-08-09 zusaetzlich verlangt: Teil-Anzeige an Elternknoten, eine
// gedeckelte Aufklapp-Aktion, die Chips der rechten Spalte und die Hervorhebung der
// Fundstelle im Treffer.
//
// Warum hier und nicht in settings.ts: settings.ts baut Markup und haengt an document.
// Alles in dieser Datei ist ohne DOM pruefbar und wird in category-modal.test.ts getestet.

import type { KategorieKatalogEintrag } from './category-path.js';
import type { KategorieBaumIndex } from './category-tree.js';

/** Lokale Maskierung, gleiche Regel wie die Kopien in settings.ts und den Nachbar-Views.
 *  Sie sitzt HIER, weil hebeSuchbegriffHervor() HTML zurueckgibt und die Maskierung damit
 *  Teil der Funktion ist und nicht Sache des Aufrufers. */
export function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;',
  );
}

// ----- Teil-Anzeige an Elternknoten ----------------------------------------
//
// Ein Elternknoten zeigt an, dass unter ihm mindestens ein Blatt gewaehlt ist. Er ist
// dabei NICHT anklickbar (Vorgabe des Betreibers): gewaehlt werden ausschliesslich
// Blattkategorien, weil Kaufland Angebote nur dort zulaesst. Die Anzeige ist reine
// Information, ein Klick auf den Knoten klappt auf oder zu.
//
// Naiv gerechnet waere das je Knoten ein Durchlauf des Teilbaums -- bei 6.090 Knoten also
// quadratisch, und bei JEDEM Rendern erneut. Stattdessen laeuft die Rechnung in der
// Gegenrichtung: von jedem gewaehlten BLATT einmal die Elternkette hinauf. Das kostet
// O(Auswahl x Tiefe) statt O(Knoten x Teilbaumgroesse); bei einigen hundert Blaettern und
// Kauflands Tiefe von unter zehn Ebenen sind das wenige tausend Map-Zugriffe.
//
// Gezaehlt wird, nicht nur markiert: Ein Zaehler laesst sich beim Ab- und Anwaehlen
// FORTSCHREIBEN (zaehlerAendern, O(Tiefe) je Klick), ein blosses Set nicht -- dort waere
// nach jedem Abwaehlen unklar, ob ein Vorfahr noch ein anderes gewaehltes Blatt traegt,
// und man muesste alles neu rechnen.

/**
 * Die Elternkette einer Kategorie, naechster Vorfahr zuerst, OHNE die Kategorie selbst.
 *
 * Abbruchbedingungen wie in `kategorienPfad`/`baueKategorieBaum`: `null` (Spalte leer),
 * `0` (Kauflands Schreibweise fuer "keine") und ein Elternteil, den der Katalog nicht
 * fuehrt. `gesehen` schuetzt gegen einen Ring in fehlerhaften Katalogdaten.
 */
export function ahnenKette(
  katalog: Map<number, KategorieKatalogEintrag>,
  id: number,
): number[] {
  const ahnen: number[] = [];
  const gesehen = new Set<number>([id]);
  let current = katalog.get(id)?.parentId ?? null;
  while (current !== null && current !== 0 && !gesehen.has(current)) {
    const eintrag = katalog.get(current);
    if (!eintrag) break;
    gesehen.add(current);
    ahnen.push(current);
    current = eintrag.parentId;
  }
  return ahnen;
}

/** category_id eines Elternknotens -> Anzahl gewaehlter Blaetter unterhalb. */
export type AhnenZaehler = Map<number, number>;

/**
 * Baut den Zaehler EINMAL aus der geladenen Auswahl. Danach wird er nur noch
 * fortgeschrieben (zaehlerAendern), nie neu gerechnet.
 */
export function baueAhnenZaehler(
  katalog: Map<number, KategorieKatalogEintrag>,
  gewaehlt: Iterable<number>,
): AhnenZaehler {
  const zaehler: AhnenZaehler = new Map();
  for (const id of gewaehlt) {
    for (const ahn of ahnenKette(katalog, id)) {
      zaehler.set(ahn, (zaehler.get(ahn) ?? 0) + 1);
    }
  }
  return zaehler;
}

/**
 * Schreibt den Zaehler fuer EIN an- oder abgewaehltes Blatt fort und liefert die
 * betroffenen Vorfahren zurueck -- der Aufrufer aktualisiert damit gezielt die
 * sichtbaren Knotenzeilen, statt den Baum neu zu rendern (ein Neu-Rendern wuerde jeden
 * aufgeklappten Ast wieder zuklappen).
 *
 * `delta` ist +1 beim Anwaehlen, -1 beim Abwaehlen. Faellt ein Zaehler auf 0, verschwindet
 * der Eintrag -- so bleibt die Map so gross wie die Zahl der tatsaechlich betroffenen
 * Vorfahren und nicht so gross wie der Katalog.
 */
export function zaehlerAendern(
  zaehler: AhnenZaehler,
  katalog: Map<number, KategorieKatalogEintrag>,
  id: number,
  delta: 1 | -1,
): number[] {
  const betroffen = ahnenKette(katalog, id);
  for (const ahn of betroffen) {
    const neu = (zaehler.get(ahn) ?? 0) + delta;
    if (neu > 0) zaehler.set(ahn, neu);
    else zaehler.delete(ahn);
  }
  return betroffen;
}

/** Ob unter diesem Knoten mindestens ein gewaehltes Blatt liegt. */
export function istTeilweiseGewaehlt(zaehler: AhnenZaehler, id: number): boolean {
  return (zaehler.get(id) ?? 0) > 0;
}

// ----- Aufklapp-Aktionen ----------------------------------------------------

/**
 * Zeilengrenze fuer "Alle aufklappen".
 *
 * Der Entwurf klappt kommentarlos ALLES auf. Bei seinem Spielzeug-Katalog sind das rund
 * 100 Zeilen, bei uns 6.090 -- 6.090 Zeilen Markup in einem `innerHTML` und anschliessend
 * 6.090 Elemente Layout im iframe. Deshalb ein harter Deckel.
 *
 * 600 ist bewusst grob und nicht feinjustiert: Es ist rund die vierzigfache Zeilenzahl des
 * Startzustands (Wurzel + 13 Kinder) und liegt weit unter der Groessenordnung, bei der das
 * Rendern spuerbar wird. Ein genauerer Wert braeuchte eine Messung im echten iframe, und
 * die gibt es bislang nicht -- siehe Changelog-Eintrag.
 */
export const KATEGORIE_AUFKLAPP_ZEILEN_GRENZE = 600;

export interface AufklappErgebnis {
  /** Der neue Aufklappzustand. Ersetzt den bisherigen vollstaendig. */
  offen: Set<number>;
  /** Wie viele Zeilen danach sichtbar sind. */
  zeilen: number;
  /** Wie viele Ebenen unterhalb der Wurzeln offen stehen. 0 = nur die Wurzeln. */
  ebenen: number;
  /** true, wenn der ganze Baum offen ist; false, wenn die Zeilengrenze gebremst hat. */
  vollstaendig: boolean;
}

/**
 * Klappt so viele VOLLSTAENDIGE Ebenen auf, wie unter der Zeilengrenze bleiben.
 *
 * Ganze Ebenen und nicht "so viele Knoten wie passen": Eine halb aufgeklappte Ebene sieht
 * aus wie ein Fehler -- der eine Ast hat Kinder, der gleichrangige daneben nicht, und
 * nichts erklaert den Unterschied. Ganze Ebenen ergeben ein Bild, das der Haendler lesen
 * kann, und eine Aussage, die sich beschriften laesst ("bis Ebene N aufgeklappt").
 *
 * Der Preis steht im Rueckgabewert und gehoert in die Oberflaeche: Bei `vollstaendig:false`
 * ist eben NICHT alles offen, und wer tiefer will, klappt den Ast einzeln auf. Ein
 * stillschweigend gedeckelter Knopf waere schlimmer als ein langsamer.
 *
 * `gesehen` schuetzt gegen einen Ring in fehlerhaften Katalogdaten, wie in
 * `sichtbareBaumZeilen`.
 */
export function ebenenweiseAufklappen(
  index: KategorieBaumIndex,
  grenze: number = KATEGORIE_AUFKLAPP_ZEILEN_GRENZE,
): AufklappErgebnis {
  const offen = new Set<number>();
  const gesehen = new Set<number>(index.wurzeln);
  let zeilen = index.wurzeln.length;
  let ebenen = 0;
  let ebene: number[] = [...index.wurzeln];

  for (;;) {
    const naechste: number[] = [];
    const eltern: number[] = [];
    for (const id of ebene) {
      const kinder = index.kinder.get(id) ?? [];
      let neu = 0;
      for (const kind of kinder) {
        if (gesehen.has(kind)) continue;
        naechste.push(kind);
        neu++;
      }
      if (neu > 0) eltern.push(id);
    }
    if (naechste.length === 0) return { offen, zeilen, ebenen, vollstaendig: true };
    if (zeilen + naechste.length > grenze) return { offen, zeilen, ebenen, vollstaendig: false };
    for (const id of eltern) offen.add(id);
    for (const kind of naechste) gesehen.add(kind);
    zeilen += naechste.length;
    ebenen++;
    ebene = naechste;
  }
}

// ----- Chips der rechten Spalte --------------------------------------------

/** Eine Zeile der Auswahl: Name oben, Elternpfad darunter -- getrennt, nicht ein Text. */
export interface KategorieChip {
  categoryId: number;
  /** Anzeigename der Kategorie selbst. */
  name: string;
  /** Brotkrumen der ELTERN, ohne die Kategorie selbst. Leer, wenn es keine gibt. */
  pfad: string;
}

/**
 * Baut die Chips der rechten Spalte, sortiert nach Elternpfad und dann nach Name -- so
 * stehen Geschwister beieinander statt alphabetisch ueber die ganze Liste verstreut.
 *
 * Den Pfad selbst baut elternPfad(); die Trefferliste zeigt denselben Text unter dem
 * Namen und darf ihn nicht abweichend zusammensetzen.
 */
/**
 * Brotkrumen der ELTERN einer Kategorie, ohne die Kategorie selbst.
 *
 * `sammelwurzelAusblenden`: Kauflands Katalog haengt alles unter EINEN Sammelknoten
 * ("Alle Kategorien" auf `de`, uebersetzt je Storefront). Dessen Name stuende sonst vor
 * jedem einzelnen Pfad und traegt nichts bei. Ausgeblendet wird er nur, wenn es genau
 * diese eine Wurzel gibt -- bei mehreren Wurzeln ist der oberste Name Information, keine
 * Wiederholung. Die Entscheidung faellt beim Aufrufer (er kennt den Baum-Index) und nicht
 * hier, damit diese Funktion ohne den Index auskommt.
 */
export function elternPfad(
  katalog: Map<number, KategorieKatalogEintrag>,
  id: number,
  sammelwurzelAusblenden: boolean,
): string {
  // ahnenKette liefert den naechsten Vorfahren zuerst, der Pfad liest sich von oben.
  const ahnen = ahnenKette(katalog, id).reverse();
  if (sammelwurzelAusblenden && ahnen.length > 0) ahnen.shift();
  return ahnen.map((a) => katalog.get(a)?.name ?? String(a)).join(' › ');
}

export function baueChips(
  katalog: Map<number, KategorieKatalogEintrag>,
  ids: Iterable<number>,
  opts: { sammelwurzelAusblenden: boolean },
): KategorieChip[] {
  const chips: KategorieChip[] = [];
  for (const id of ids) {
    chips.push({
      categoryId: id,
      name: katalog.get(id)?.name ?? String(id),
      pfad: elternPfad(katalog, id, opts.sammelwurzelAusblenden),
    });
  }
  chips.sort((a, b) => {
    const p = a.pfad.localeCompare(b.pfad, 'de');
    if (p !== 0) return p;
    const n = a.name.localeCompare(b.name, 'de');
    return n !== 0 ? n : a.categoryId - b.categoryId;
  });
  return chips;
}

// ----- Hervorhebung der Fundstelle -----------------------------------------

/**
 * Markiert das erste Vorkommen des Suchbegriffs mit `<mark>` und maskiert ALLES uebrige.
 *
 * Die Maskierung steckt absichtlich in dieser Funktion: Sie gibt HTML zurueck, der
 * Aufrufer darf also nicht noch einmal maskieren, und er darf es auch nicht vergessen.
 * Ein Kategoriename kommt aus `kaufland.categories` und damit von aussen.
 *
 * Zwei Faelle brechen bewusst ohne Hervorhebung ab, statt falsch zu schneiden: Wenn
 * `toLowerCase()` die LAENGE aendert (etwa 'İ' U+0130, das zu zwei Zeichen wird), stimmen
 * die Indizes der Kleinschreibung nicht mehr mit denen des Originals ueberein. Ein
 * Ausschnitt daraus zerschnitte den Text an der falschen Stelle -- im harmlosen Fall
 * verrutscht die Markierung, im schlechten Fall mitten in einer Entitaet. Lieber keine
 * Markierung als eine falsche.
 */
export function hebeSuchbegriffHervor(text: string, begriff: string): string {
  const b = begriff.trim();
  if (b === '') return escapeHtml(text);
  const kleinText = text.toLowerCase();
  const kleinBegriff = b.toLowerCase();
  if (kleinText.length !== text.length || kleinBegriff.length !== b.length) {
    return escapeHtml(text);
  }
  const i = kleinText.indexOf(kleinBegriff);
  if (i < 0) return escapeHtml(text);
  const ende = i + b.length;
  return (
    escapeHtml(text.slice(0, i)) +
    '<mark>' +
    escapeHtml(text.slice(i, ende)) +
    '</mark>' +
    escapeHtml(text.slice(ende))
  );
}

// ----- Vererbung auf andere aktive Kanaele ---------------------------------
//
// Kauflands Kategoriebaum ist ueber alle Storefronts identisch (2026-08-16 gemessen: neun
// gecrawlte Storefronts, je 6.090 Kategorien, ein einziger Fingerprint ueber alle Baeume).
// Eine einmal gewaehlte category_id ist deshalb in jedem Kanal gueltig, und die Auswahl laesst
// sich uebertragen, ohne sie neu zusammenzusuchen.
//
// Was NICHT folgt: dass die Auswahl ueberall gleich sein soll. Migration 0043 hat genau diese
// Annahme zurueckgenommen -- ein Haendler verkauft in Polen andere Kategorien als in
// Deutschland. Die Vererbung ergaenzt deshalb, sie gleicht nicht ab.

/**
 * Die Zielkanaele einer Vererbung: alle uebergebenen Storefronts ausser der Quelle.
 *
 * Normalisiert auf Kleinschreibung ohne Rand-Leerzeichen und entfernt Dubletten, weil die
 * Kacheln ihre Codes aus zwei Quellen speisen (Zeilen aus `account.kaufland_storefronts` und
 * das Probe-Ergebnis der Edge Function). Die Reihenfolge der Eingabe bleibt erhalten, damit
 * die Anzeige der Kachel-Reihenfolge folgt.
 */
export function vererbungsZiele(alle: string[], quelle: string): string[] {
  const q = quelle.trim().toLowerCase();
  const gesehen = new Set<string>();
  const ziele: string[] = [];
  for (const roh of alle) {
    const code = roh.trim().toLowerCase();
    if (code === '' || code === q || gesehen.has(code)) continue;
    gesehen.add(code);
    ziele.push(code);
  }
  return ziele;
}

/** Ergebnis der RPC `kaufland_inherit_tenant_categories`, je Storefront. */
export interface VererbungsErgebnis {
  [storefront: string]: { added: number; skipped: number };
}

/**
 * Text fuer die Fusszeile nach dem Vererben.
 *
 * Uebersprungene Kanaele werden NAMENTLICH genannt. Ein Kanal ohne Katalog (`hr`, wo Kaufland
 * auf `GET /v2/categories` mit HTTP 400 antwortet) bekommt keine einzige Zeile — ohne diesen
 * Hinweis bliebe unklar, warum er leer bleibt, und der naechste Verdacht faellt auf die
 * Vererbung selbst.
 */
export function vererbungsMeldung(ergebnis: VererbungsErgebnis): string {
  const kanaele = Object.keys(ergebnis);
  if (kanaele.length === 0) return '';

  const uebertragen = kanaele.filter((k) => ergebnis[k]!.added > 0 || ergebnis[k]!.skipped === 0);
  const neu = kanaele.reduce((summe, k) => summe + ergebnis[k]!.added, 0);
  const uebersprungen = kanaele.filter((k) => ergebnis[k]!.added === 0 && ergebnis[k]!.skipped > 0);

  const wort = uebertragen.length === 1 ? 'Kanal' : 'Kanäle';
  let text = `Auf ${uebertragen.length} ${wort} vererbt (${neu} neu).`;
  if (uebersprungen.length > 0) {
    text += ` ${uebersprungen.map((k) => k.toUpperCase()).join(', ')} übersprungen: kein Katalog.`;
  }
  return text;
}
